import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SigninRequest, SignupRequest } from '@interfaces/auth.interface';
import { UpdateCurrentUserRequest } from '@interfaces/user.interface';

import {
  useLogoutMutation,
  useSigninMutation,
  useSignupMutation,
} from '@api/auth.api';
import api from '@api/index';
import { useUpdateCurrentUserMutation } from '@api/users.api';

import {
  logout as logoutAction,
  selectCurrentUser,
  setCredentials,
} from '@slices/auth.slice';

const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);

  const [signupMutation, { isLoading: isLoadingSignup }] = useSignupMutation();

  const [signinMutation, { isLoading: isLoadingSignin }] = useSigninMutation();
  const [logoutMutation, { isLoading: isLoadingLogout }] = useLogoutMutation();
  const [patchMutation, { isLoading: isLoadingPatch }] =
    useUpdateCurrentUserMutation();

  const signin = useCallback(
    async (data: SigninRequest) => {
      const user = await signinMutation(data).unwrap();

      dispatch(setCredentials(user));
    },
    [dispatch, signinMutation],
  );

  const signup = useCallback(
    async (body: SignupRequest) => {
      await signupMutation(body).unwrap();
    },
    [signupMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation().unwrap();

    dispatch(api.util.resetApiState());
    dispatch(logoutAction());
  }, [dispatch, logoutMutation]);

  const patch = useCallback(
    async (body: UpdateCurrentUserRequest) => {
      const patchedUser = await patchMutation(body).unwrap();
      const updatedUser = {
        ...user,
        ...patchedUser,
      };
      dispatch(setCredentials(updatedUser));
    },
    [dispatch, patchMutation, user],
  );

  return useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      signin,
      signup,
      patch,
      logout,
      isLoading:
        isLoadingSignin || isLoadingSignup || isLoadingPatch || isLoadingLogout,
    }),
    [
      user,
      signin,
      patch,
      signup,
      logout,
      isLoadingSignin,
      isLoadingPatch,
      isLoadingLogout,
      isLoadingSignup,
    ],
  );
};

export default useAuth;
